// The three public legal documents, as data rather than JSX (1 Aug 2026).
//
// Counsel delivered these on 1 Aug 2026 in response to docs/legal-review-brief.md;
// the source of record is docs/legal/Practice_Legal_Launch_Package.md and the
// .docx set beside it. The text here is theirs, transcribed, not ours to edit:
// if a sentence needs to change, it changes there first and then here.
//
// Structured rather than written as components for three reasons. Each
// document has a `version`, which the consent record has to name (counsel's
// signup flow requires storing which version a user accepted). Each renders
// in two places, a modal and a route, and prose kept in two copies drifts.
// And the em dash sweep, the "Lab 10" spacing rule and the reader-facing
// "practice" rule can all be checked over a plain string array.
//
// DATED THE DAY COUNSEL DELIVERED THEM (1 Aug 2026, Rob). They were carried
// as undated drafts for an afternoon, which meant the page either showed a
// raw {{EFFECTIVE_DATE}} token or a banner explaining that it was not live
// yet. Both were us talking about ourselves on a page about the reader's
// rights. A document in the product is in effect for anyone reading it now.
//
// STILL OPEN: the DMCA agent block is complete as text (title, address, phone,
// email, 2 Aug 2026) but the designation is not filed with the US Copyright
// Office, and the safe harbor needs both. Nothing in the document says so,
// because a public listing that hedges is worse than one that does not: the
// gap is tracked in docs/legal-launch-engineering.md section 3 instead. If
// the filed entry ever differs from these four lines, the document is the one
// that has to move, and TERMS_VERSION with it.

export const LEGAL_ENTITY = "Lab 10, Inc.";
export const LEGAL_ENTITY_LONG = "Lab 10, Inc., a Nevada corporation";
export const NOTICE_ADDRESS = "6130 W Flamingo Rd 861, Las Vegas, NV 89103";
export const LEGAL_EMAIL = "hello@lab10.com";
// One date for all three, shown under each title. Change it whenever the
// words change, alongside that document's version.
export const EFFECTIVE_DATE = "1 August 2026";
/* A title, not a person (2 Aug 2026, Rob asked which it had to be). 17 U.S.C.
   512(c)(2) asks for the agent's name, address, phone number and email; the
   Copyright Office's own regulation for the directory (37 C.F.R. 201.38)
   takes a position, a department or a third-party service in place of an
   individual, which is what most companies file and what keeps the filing
   from going stale the day that individual leaves.

   Naming the title here does not make the designation. The safe harbor needs
   BOTH this public listing and a registration in the Copyright Office's
   directory, which is a fee and a renewal every three years, and that is
   still outstanding (docs/legal-launch-engineering.md section 3).

   The telephone number is the fourth thing the statute lists and was missing
   until 2 Aug 2026, when Rob supplied it. It has to match what goes in the
   directory: the two are one designation, and a number that is right in one
   place and wrong in the other is worse than either. */
export const DMCA_AGENT = "Copyright Agent, Lab 10, Inc.";
export const DMCA_AGENT_PHONE = "(302) 665-2210";

// Bump the version of a document whenever its words change. Consent records
// store these, so an old record keeps naming the text the user actually saw.
// Not rendered on the page: a reader needs the effective date, and a version
// string formatted as a date only reads as a second one (Rob, 1 Aug 2026).
export const TERMS_VERSION = "2026-08-01";
/* Bumped 3 Aug 2026 for the first-party analytics wording (see
   docs/lab10-analytics-plan.md). CLAUDE.md's rule is that a document's version
   moves whenever its words do, and it moves here even though no consent record
   names the old one yet, because the point of the rule is that the trail
   exists before it is needed rather than after.

   Drafted by us against what the code actually does, then REVIEWED AND
   APPROVED BY COUNSEL (3 Aug 2026, via Rob). So these paragraphs are counsel's
   now like the rest of the document, and the standing rule applies to them
   again: they change in docs/legal/ first and here second, never the other way
   round.

   EFFECTIVE_DATE was left at 1 August 2026 through that review. It is one
   constant for all three documents and the Terms did not change, so moving it
   would have redated a document nobody touched. Flagged to Rob rather than
   decided here; if counsel wants the two changed documents to carry their own
   later date, they need their own constant. */
export const PRIVACY_VERSION = "2026-08-03";
export const CHD_VERSION = "2026-08-03";

export type LegalBlock =
  | { h: string }
  | { h3: string }
  | { p: string }
  | { ul: string[] }
  | { address: string[] };

export interface LegalDocument {
  id: LegalDocId;
  title: string;
  version: string;
  // Rendered under the title, as the only date on the page.
  effective: string;
  blocks: LegalBlock[];
}

export type LegalDocId = "terms" | "privacy" | "health";

const CONTACT_ADDRESS = [LEGAL_ENTITY, NOTICE_ADDRESS, LEGAL_EMAIL];

export const TERMS: LegalDocument = {
  id: "terms",
  title: "Terms of Use",
  version: TERMS_VERSION,
  effective: EFFECTIVE_DATE,
  blocks: [
    {
      p: `These Terms of Use are a binding agreement between you and ${LEGAL_ENTITY}, doing business as Lab 10 ("Lab 10," "we," "us," or "our"). They govern your use of Practice at practice.lab10.com and related services.`,
    },
    {
      p: "You may use Practice only if you are at least 18 years old, are located in the United States, and can enter a binding contract. By checking the acceptance box and requesting a sign-in link, you agree to these Terms. Our Privacy Policy and Consumer Health Data Privacy Policy explain how we handle data.",
    },
    { h: "1. What Practice does" },
    {
      p: "Practice uses automated systems to find, extract, quote, summarize, paraphrase, label, rank, and organize material published by third parties. It uses your selections and activity to create a personalized set of practices and other content.",
    },
    {
      p: "The people, publications, and organizations shown in Practice are sources or subjects. Unless we clearly say otherwise, they are not affiliated with Lab 10, have not reviewed or approved Practice, and do not sponsor or endorse Lab 10 or Practice.",
    },
    {
      p: "Most content is generated and checked by automated systems. Most items are not reviewed by a clinician or human editor before they appear. Summaries, labels, cautions, rankings, comparisons, and inferred positions are editorial outputs from Lab 10. They may be incomplete, wrong, outdated, or missing context. When a source and Practice differ, rely on the source.",
    },
    { h: "2. Practice is not medical care" },
    {
      p: "Practice is for general informational and educational use. It is not medical advice, diagnosis, treatment, a prescription, or a substitute for a qualified clinician who knows you. Using Practice does not create a doctor-patient, therapist-patient, pharmacist-patient, or other professional relationship with Lab 10 or any person shown in Practice.",
    },
    {
      p: "Practice may surface content about supplements, fasting, restrictive diets, hormones, chelation, vaccines, mental health, sexual health, fertility, and other subjects that can involve meaningful risks or interactions. Some sources may sell products or services they discuss. Evidence labels are editorial shorthand, not medical grades or guarantees of safety, effectiveness, or scientific consensus.",
    },
    {
      p: "Do not start, stop, or change a medication, supplement, treatment, diet, fasting protocol, vaccination decision, or other health practice based only on Practice. Talk with a qualified clinician first, especially if you are pregnant, nursing, taking medication, preparing for a procedure, or living with a medical or mental health condition.",
    },
    {
      p: "Practice is not designed for emergencies or crisis care. Do not use it to decide whether to seek urgent help. In the United States, call 911 for an emergency. Call or text 988 for a suicide or mental health crisis.",
    },
    { h: "3. Your account" },
    {
      p: `You sign in through a link sent to your email. You are responsible for controlling that inbox, device, and session. Do not let another person use your account. Tell us promptly at ${LEGAL_EMAIL} if you believe your account or sign-in link was misused.`,
    },
    {
      p: "You must provide accurate information and keep it current. You may not create an account for a person under 18 or use Practice on another person's behalf without legal authority.",
    },
    { h: "4. Your decisions and your content" },
    {
      p: "You decide whether to add, complete, snooze, decline, or follow any practice. Lab 10 does not monitor your health or know whether a practice is appropriate for you.",
    },
    {
      p: "You own the practices, text, photographs, and other material you upload or write. You give Lab 10 a limited, worldwide, royalty-free license to host, copy, convert, display, and otherwise process that material only as needed to operate, secure, support, and improve the reliability of Practice for you. This license ends when the material is deleted from active systems, subject to limited retention in backups, security records, or as required by law.",
    },
    {
      p: "You promise that you have the rights needed to upload your content and that it does not violate law or another person's rights. Do not upload another person's health information, private information, or image without authority.",
    },
    {
      p: "If you send ideas or feedback about Practice, you allow Lab 10 to use them without restriction or payment. We will not identify you publicly as the source without permission.",
    },
    { h: "5. Acceptable use" },
    {
      p: "You may use Practice only for your personal, noncommercial, informational use. You may not:",
    },
    {
      ul: [
        "use Practice to diagnose, treat, prescribe for, or make health decisions about another person;",
        "scrape, crawl, index, bulk download, copy, republish, sell, or commercially exploit Practice or its outputs;",
        "use automated access, bypass rate limits, reverse engineer the service, or use its outputs to train or evaluate a model without our written permission;",
        "impersonate another person, access another account, or interfere with security or operation;",
        "upload unlawful, infringing, deceptive, harmful, or malicious material; or",
        "use Practice in a way that violates law or another person's rights.",
      ],
    },
    {
      p: "We may investigate suspected misuse and take reasonable steps to protect users, sources, rights holders, and the service.",
    },
    { h: "6. Ownership and source rights" },
    {
      p: "Lab 10 owns Practice, including its software, design, organization, evidence labels, rankings, editorial text, and other original material. We give you a limited, revocable, nontransferable license to use Practice as these Terms allow.",
    },
    {
      p: "Third parties own their articles, recordings, photographs, trademarks, names, likenesses, and other source material. A link or attribution does not transfer those rights to you. Your use of source material remains subject to law and any terms set by its owner.",
    },
    {
      p: "Names, titles, photographs, quotations, and trademarks are used to identify or report on people, sources, and subjects. Their appearance does not by itself mean affiliation, approval, sponsorship, or endorsement.",
    },
    { h: "7. Corrections, rights notices, and copyright notices" },
    {
      p: `Practice can get an attribution, quotation, summary, label, or other detail wrong. You may report an issue at ${LEGAL_EMAIL}. Please include the Practice page, source link, the specific issue, and supporting information. We may investigate, correct, relabel, remove, or leave the material unchanged. We do not promise a particular result or response time, but we aim to handle credible reports responsibly.`,
    },
    {
      p: "A person shown in Practice, or an authorized rights holder, may use the same address to request review of a name, likeness, quotation, or source.",
    },
    {
      p: "To send a copyright notice under the Digital Millennium Copyright Act, contact our designated agent:",
    },
    // Name, address, phone, email: the four the statute lists, in its order.
    { address: [DMCA_AGENT, NOTICE_ADDRESS, DMCA_AGENT_PHONE, LEGAL_EMAIL] },
    {
      p: "Your notice must include: your physical or electronic signature; identification of the copyrighted work; identification and location of the material you want removed; your contact information; a good-faith statement that the use is not authorized; and a statement, under penalty of perjury, that the notice is accurate and you are authorized to act. We may remove material and terminate repeat infringers where appropriate. False notices may create liability.",
    },
    { h: "8. Changes, availability, and termination" },
    {
      p: "Practice is evolving. We may correct, add, remove, relabel, suspend, or discontinue content or features. We do not guarantee that any source, expert, practice, feature, or account history will always remain available.",
    },
    {
      p: "Practice is free at launch. We will not charge you for the features available to your account when you first accept these Terms. Any future charge will be for an optional new offering that you affirmatively choose after seeing its price and terms.",
    },
    {
      p: "You may stop using Practice or delete your account at any time. We may restrict, suspend, or terminate access if we reasonably believe you violated these Terms, created legal or security risk, or threatened the service or another person. Where practical, we will give notice and a chance to export your data, unless doing so would create risk or violate law.",
    },
    { h: "9. No warranties" },
    {
      p: 'To the fullest extent allowed by law, Practice is provided "as is" and "as available." Lab 10 disclaims implied warranties of merchantability, fitness for a particular purpose, title, noninfringement, and any warranty arising from course of dealing or usage.',
    },
    {
      p: "We do not warrant that Practice or any source material is accurate, complete, current, available, secure, error-free, or appropriate for you. We do not warrant that any practice is safe or effective, that any evidence label reflects consensus, or that a source is qualified, unbiased, or free of conflicts. Some jurisdictions do not allow certain disclaimers, so parts of this section may not apply to you.",
    },
    { h: "10. Limits on liability" },
    {
      p: "To the fullest extent allowed by law, Lab 10 and its officers, directors, employees, contractors, affiliates, and licensors will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost profits, data, goodwill, or opportunities.",
    },
    {
      p: "To the fullest extent allowed by law, our total liability for all claims arising from or relating to Practice or these Terms will not exceed the greater of $100 or the amount you paid Lab 10 for Practice during the 12 months before the event giving rise to the claim.",
    },
    {
      p: "These limits apply regardless of the legal theory and even if a remedy fails of its essential purpose. They do not limit liability that law does not allow us to limit, including liability for fraud, willful misconduct, or certain personal injury claims. Your local law may give you additional rights.",
    },
    { h: "11. Indemnity" },
    {
      p: "To the extent allowed by law, you will defend and indemnify Lab 10 and its officers, directors, employees, contractors, and affiliates against third-party claims, losses, and reasonable costs caused by your unlawful use of Practice, your content, or your violation of these Terms or another person's rights. This does not apply to the extent a claim was caused by Lab 10's own negligence or misconduct.",
    },
    { h: "12. Individual arbitration and class action waiver" },
    {
      p: "Please read this section carefully. It affects your right to go to court, have a jury decide a dispute, or participate in a class action.",
    },
    { h3: "Informal resolution first" },
    {
      p: `Before filing a claim, the complaining party must send a written notice describing the dispute, the account email, supporting facts, and the requested relief. Send notices to ${LEGAL_EMAIL} and ${NOTICE_ADDRESS}. The parties will try in good faith to resolve the dispute for 30 days. This step does not apply when immediate relief is needed to prevent serious harm or preserve a claim.`,
    },
    { h3: "Arbitration agreement" },
    {
      p: "Except for the matters listed below, you and Lab 10 agree that any dispute arising from or relating to Practice, these Terms, or our relationship will be resolved by binding individual arbitration administered by the American Arbitration Association under its Consumer Arbitration Rules. If applicable, the AAA Mass Arbitration Supplementary Rules will also apply. The Federal Arbitration Act governs this arbitration agreement.",
    },
    {
      p: "The arbitration may take place by video, telephone, written submissions, or in the county where you live, as the rules allow. Lab 10 will pay arbitration fees it is required to pay under the consumer rules. The arbitrator may award the same individual remedies a court could award.",
    },
    {
      p: "The court, not the arbitrator, will decide whether an arbitration agreement was formed and whether the class waiver is enforceable. The arbitrator will decide other questions about the scope or enforceability of this section.",
    },
    { h3: "Claims that may stay in court" },
    {
      p: "Either party may bring an individual claim in small claims court. Either party may seek temporary or other relief in court to protect intellectual property, account security, or prevent imminent harm. A request for public injunctive relief may proceed in court to the extent applicable law does not allow it to be arbitrated.",
    },
    { h3: "No class or representative proceedings" },
    {
      p: "You and Lab 10 agree to bring claims only on an individual basis. Neither party may bring or participate in a class, collective, consolidated, or representative action or arbitration. An arbitrator may award relief only to the individual party seeking it and only as needed for that person's claim. Administrative coordination allowed by the AAA Mass Arbitration Supplementary Rules does not create a class arbitration.",
    },
    { h3: "Your right to opt out" },
    {
      p: `You may opt out of this arbitration agreement by emailing ${LEGAL_EMAIL} within 30 days after you first accept these Terms. Use the subject "Arbitration Opt-Out" and include your full name, account email, and a clear statement that you opt out. Opting out will not affect your account or the rest of these Terms.`,
    },
    {
      p: "If AAA will not administer an arbitration under these terms, the parties will select another nationally recognized provider that applies consumer protections. If they cannot agree, a court may appoint one.",
    },
    { h: "13. Governing law and general terms" },
    {
      p: "Nevada law governs these Terms, without regard to conflict-of-law rules, except that the Federal Arbitration Act governs the arbitration section. Mandatory consumer protections in your home state still apply. Any claim that is not subject to arbitration must be brought in the state or federal courts located in Clark County, Nevada, unless applicable law requires another forum. You and Lab 10 consent to those courts.",
    },
    {
      p: "We may update these Terms prospectively. We will give reasonable notice of material changes and ask you to accept them when required. A change to the arbitration section will not apply to a dispute for which either party already gave written notice. You may reject a future arbitration change by following the opt-out instructions in the notice.",
    },
    {
      p: "You may not transfer your account or these Terms. We may assign these Terms in connection with a reorganization, financing, merger, sale, or transfer of Practice. If one provision is unenforceable, the rest remains effective, except as stated in the class waiver. A delay in enforcement is not a waiver. These Terms, the policies they incorporate, and any terms for an optional paid offering are the entire agreement about Practice.",
    },
    {
      p: "Sections that by their nature should survive termination will survive, including ownership, disclaimers, liability limits, indemnity, disputes, and general terms. We are not responsible for delay or failure caused by events beyond our reasonable control.",
    },
    { p: "Questions or legal notices:" },
    { address: CONTACT_ADDRESS },
  ],
};

export const PRIVACY: LegalDocument = {
  id: "privacy",
  title: "Privacy Policy",
  version: PRIVACY_VERSION,
  effective: EFFECTIVE_DATE,
  blocks: [
    {
      p: `${LEGAL_ENTITY}, doing business as Lab 10 ("Lab 10," "we," "us," or "our"), operates Practice at practice.lab10.com. This policy explains how Practice handles personal information. Our separate Consumer Health Data Privacy Policy gives additional detail and rights for health-related data.`,
    },
    {
      p: "Practice is intended only for adults in the United States. It is not a health care provider or health plan, and this policy is not a HIPAA Notice of Privacy Practices.",
    },
    { h: "The short version" },
    {
      p: "We collect your email, the health-related choices and activity needed to personalize Practice, content you create, and limited technical data needed to sign you in, keep the service secure, and measure how Practice is used. We do not sell personal information, use it for targeted advertising, or run third-party advertising trackers. We measure how Practice is used with first-party analytics software that Lab 10 hosts on its own infrastructure, which sets no cookie, stores no identifier on your device, and sends nothing to an analytics company. Google Cloud hosts Practice, and SendGrid delivers sign-in email.",
    },
    { h: "Information we collect" },
    { h3: "Information you give us" },
    {
      ul: [
        "Your email address, which is your account identifier and sign-in method.",
        "The health areas, topic filters, experts, and other onboarding choices you select.",
        "Practices you add, completion history, snoozes, declines, mutes, followed experts, and Crossfire stances.",
        "Practices, text, and photographs you create or upload.",
        "Messages, correction reports, privacy requests, and other communications you send us.",
      ],
    },
    { h3: "Information collected when you use Practice" },
    {
      ul: [
        "Session and sign-in events.",
        "Standard connection and security data, such as IP address, browser type, device type, request time, and error or security logs.",
        "Your timezone, which may be suggested from your browser.",
        "Information about how you move through and use Practice, to the extent needed to save your state, provide the service, and diagnose errors.",
        "Analytics records: the address and title of pages you view within Practice, the site that referred you, and your browser, operating system, device type, screen size, and language. Your IP address is used to work out an approximate location and a daily session identifier and is not stored in these records. Analytics records are not linked to your account.",
      ],
    },
    {
      p: "Practice does not ask you for a diagnosis, symptoms, medications, biometric data, or laboratory results. At the effective date, Practice does not use third-party analytics, advertising technology, tracking pixels, or marketing cookies. The analytics software Practice uses is first-party: Lab 10 runs it on Lab 10 infrastructure, your browser sends analytics data only to practice.lab10.com, and no analytics vendor receives it. If that changes, we will update this policy and ask for consent where required.",
    },
    { h: "How we use information" },
    { p: "We use personal information to:" },
    {
      ul: [
        "create and personalize your daily practice, feed, source discovery, and other features;",
        "remember your choices, history, and preferences;",
        "authenticate you, keep your session active, and send sign-in links;",
        "measure how Practice is used, so we can see which parts work and improve them;",
        "operate, secure, troubleshoot, and support Practice;",
        "respond to questions, rights requests, and reports about content;",
        "prevent misuse and enforce our Terms of Use; and",
        "comply with law and protect users, sources, rights holders, Lab 10, and others.",
      ],
    },
    {
      p: "We do not use your health-related information to target advertising, determine eligibility for employment, credit, housing, or insurance, or sell products on another company's behalf.",
    },
    { h: "Automated processing" },
    {
      p: "Automated systems use your selections and activity to rank and choose what Practice shows you. Automated systems also extract, summarize, label, and organize third-party material. This processing personalizes content. It does not make decisions about your legal rights, employment, credit, housing, insurance, or access to health care. Most source summaries are not reviewed by a clinician or human editor before publication.",
    },
    { h: "When we disclose information" },
    { p: "We disclose personal information only as described here:" },
    {
      ul: [
        "Service providers. Google Cloud hosts our application, database, and private file storage in the United States. SendGrid receives your email address and sign-in delivery information to send sign-in links. Other vendors may process limited information for security, support, legal, or infrastructure services under contractual restrictions.",
        "Professional advisers. Lawyers, auditors, insurers, and similar advisers may receive information when reasonably needed for their work and subject to duties of confidentiality.",
        "Legal and safety reasons. We may disclose information when reasonably necessary to comply with law, respond to valid legal process, investigate fraud or security issues, protect rights or safety, or enforce our agreements.",
        "A business transaction. Information may transfer as part of a financing, reorganization, merger, acquisition, sale, insolvency, or transfer of Practice. A recipient must handle it consistently with this policy unless law and any required consent allow otherwise.",
        "At your direction. We may disclose information when you ask us to or give specific consent.",
      ],
    },
    {
      p: "We do not sell personal information. We do not share personal information for cross-context behavioral advertising or targeted advertising. We do not allow third parties to track you across unrelated websites or apps through Practice.",
    },
    {
      p: "For California law, the categories collected in the preceding 12 months are identifiers, internet or electronic activity, user-created content, and sensitive personal information consisting of health-related choices and inferences. We disclose these categories to the service providers described above only as needed for the stated purposes. We have not sold or shared them for cross-context behavioral advertising.",
    },
    { h: "Cookies and email" },
    {
      p: "Practice uses one essential session cookie, called practice_session, to keep you signed in. Blocking it may prevent Practice from working. Our analytics uses no cookie and stores nothing on your device. We send account and sign-in email, not newsletters or marketing email.",
    },
    { h: "How long we keep information" },
    {
      p: "We keep account information, choices, activity, and user content while your account is open. When you delete your account, we remove account-linked information from active systems promptly, generally within 30 days. Residual copies may remain in protected backups for up to six months and are not used except for disaster recovery, security, or legal needs. They are deleted when the backups expire or are overwritten.",
    },
    {
      p: "We generally keep routine security and email-delivery logs for up to 90 days. We keep analytics records for 12 months, after which they are deleted automatically. We may keep a record of consents, privacy requests, appeals, legal notices, and account deletion for up to six years to demonstrate compliance or resolve disputes. We may keep information longer when law requires it, a legal hold applies, or it is reasonably needed to investigate fraud or security abuse.",
    },
    { h: "Security" },
    {
      p: "We use reasonable administrative, technical, and physical safeguards appropriate to the information we hold. These include restricted access, private cloud storage for user uploads, and removal of image metadata from uploaded photographs. No system is perfectly secure. We will notify affected people and regulators of a qualifying incident as required by law.",
    },
    { h: "Your choices and rights" },
    {
      p: `You may use account settings to export your account information or delete your account. You may also email ${LEGAL_EMAIL} to ask to access, correct, delete, or obtain a copy of personal information, withdraw a consent, or appeal a denied request.`,
    },
    {
      p: "We verify requests through the email linked to your account or another reasonable method. We usually respond within 45 days. We may extend once when law allows and will explain why. If we deny a request, we will explain the decision and how to appeal. We do not discriminate against you for exercising a privacy right.",
    },
    {
      p: "Some state laws provide additional rights, including rights to know categories and recipients, correct inaccurate information, limit certain uses of sensitive information, opt out of sale or targeted advertising, and use an authorized agent. We do not sell or use personal information for targeted advertising, so those opt-outs do not currently apply. We honor legally required browser-based opt-out signals where applicable.",
    },
    {
      p: "For health-related information, see the Consumer Health Data Privacy Policy. It explains additional rights to confirm, access, withdraw consent, delete, and appeal.",
    },
    { h: "Children" },
    {
      p: `Practice is not offered to anyone under 18. We do not knowingly collect personal information from a person under 18. If you believe a minor has used Practice, contact ${LEGAL_EMAIL} so we can investigate and delete the account where appropriate.`,
    },
    { h: "United States service" },
    {
      p: "Practice is intended for use only in the United States. Information is processed and stored in the United States. Do not use Practice if you are outside the United States.",
    },
    { h: "Changes to this policy" },
    {
      p: "We may update this policy as Practice changes. We will post the new effective date and give additional notice before a material change when law requires it. We will obtain consent before using consumer health data for a materially new purpose when required by law.",
    },
    { h: "Contact us" },
    { address: CONTACT_ADDRESS },
    {
      p: `Privacy requests and appeals may be sent to ${LEGAL_EMAIL}. Use the subject "Privacy Request" or "Privacy Appeal."`,
    },
  ],
};

export const CONSUMER_HEALTH: LegalDocument = {
  id: "health",
  title: "Consumer Health Data Privacy Policy",
  version: CHD_VERSION,
  effective: EFFECTIVE_DATE,
  blocks: [
    {
      p: `This Consumer Health Data Privacy Policy is provided by ${LEGAL_ENTITY}, doing business as Lab 10 ("Lab 10," "we," "us," or "our"). It applies to Practice at practice.lab10.com and is separate from our general Privacy Policy.`,
    },
    {
      p: 'This policy explains how we handle "consumer health data" under Washington, Nevada, and similar state laws. We use the term broadly for data linked to you that reveals, suggests, or supports an inference about physical or mental health.',
    },
    { h: "Consumer health data we collect" },
    { p: "Practice may collect or infer:" },
    {
      ul: [
        "the health areas and topics you select or mute;",
        "the experts and sources you follow;",
        "practices you add, complete, snooze, decline, or write;",
        "your Crossfire choices and other expressed views about health topics;",
        "photographs or text you attach to a practice;",
        "the timing and pattern of the activity above; and",
        "health interests or preferences inferred from those choices and activity.",
      ],
    },
    {
      p: "We do not ask for diagnoses, symptoms, medications, biometric data, or laboratory results.",
    },
    { h: "Sources of consumer health data" },
    {
      p: "We receive this data from your selections, content, messages, and use of Practice. We may infer topic preferences from your activity. We do not buy consumer health data or obtain it from health care providers, pharmacies, insurers, or connected medical devices.",
    },
    { h: "Why we collect and use it" },
    { p: "We collect and use consumer health data to:" },
    {
      ul: [
        "create, rank, and personalize the practices and source material shown to you;",
        "remember your choices, progress, snoozes, declines, and mutes;",
        "display your own practices and history;",
        "measure how Practice is used, in records that are not linked to your account;",
        "operate, secure, troubleshoot, and support Practice;",
        "respond to privacy requests and content reports; and",
        "comply with law and protect rights and safety.",
      ],
    },
    {
      p: "Automated systems use this data to choose and order content. They do not diagnose you or decide your access to care, insurance, employment, credit, housing, or another legal right.",
    },
    {
      p: "Our analytics records the addresses of pages viewed within Practice, and a page address can suggest a health interest. Those records are held on Lab 10 infrastructure, are not linked to your account, are not disclosed to any third party, and are deleted after 12 months. Search terms are removed in your browser and never reach them.",
    },
    { h: "Consent" },
    {
      p: "Where law requires consent, we ask you to opt in before collecting or using consumer health data for personalization. The consent request is separate from acceptance of the Terms of Use. You may decline, but Practice cannot create a personalized account without the data described above.",
    },
    {
      p: `You may withdraw consent through account settings or by emailing ${LEGAL_EMAIL}. Withdrawal stops future consent-based collection and use. You may also delete your account and data.`,
    },
    {
      p: "We will ask for new consent before collecting a materially new category of consumer health data or using it for a materially different purpose when law requires it.",
    },
    { h: "When we disclose consumer health data" },
    {
      p: "We disclose consumer health data only as needed to provide a service you request, with your separate consent where required, or as otherwise allowed by law.",
    },
    {
      p: "Data disclosed may include health selections, practice activity, user-created content, your account identifier, and related technical records. Recipients may include:",
    },
    {
      ul: [
        "Google Cloud, which hosts Practice, the database, and private user uploads in the United States;",
        "contractors and infrastructure providers with limited, contractually restricted access needed to operate, secure, or support Practice;",
        "lawyers, insurers, auditors, and similar confidential advisers when needed;",
        "authorities or others when needed to comply with law, valid legal process, or protect rights or safety; and",
        "a buyer or successor in a financing, reorganization, merger, acquisition, insolvency, or sale, subject to law and required consent.",
      ],
    },
    {
      p: "SendGrid receives only your email and sign-in delivery data, not health selections or practice history. No Lab 10 affiliate receives consumer health data at launch.",
    },
    {
      p: "No third party is allowed to collect consumer health data through Practice over time or across unrelated websites or apps. We do not sell consumer health data. We do not disclose it for targeted advertising.",
    },
    { h: "Your consumer health data rights" },
    { p: "Subject to applicable law, you may:" },
    {
      ul: [
        "confirm whether we collect or disclose your consumer health data;",
        "access the data, including a list and available contact information for third parties or affiliates that received it;",
        "correct inaccurate data;",
        "withdraw consent to collection, use, or disclosure;",
        "delete the data, including data held by processors; and",
        "appeal a denial of your request.",
      ],
    },
    {
      p: `Use account settings or email ${LEGAL_EMAIL} with the subject "Consumer Health Data Request." We verify requests through your account email or another reasonable method. An authorized agent may submit a request with proof of authority.`,
    },
    {
      p: `We generally respond within 45 days and may extend once when law permits. If we deny a request, we explain why and how to appeal. Send an appeal to ${LEGAL_EMAIL} with the subject "Consumer Health Data Appeal." If we deny an appeal, we will provide the applicable attorney general contact or complaint method where required. We do not discriminate against you for exercising these rights.`,
    },
    { h: "Deletion and retention" },
    {
      p: "We keep consumer health data while your account is open. After a verified deletion request or account deletion, we remove it from active systems, generally within 30 days, and direct our processors to do the same. Protected backups may take up to six months to expire and are used only for recovery, security, or legal needs.",
    },
    {
      p: "We may keep limited compliance, request, deletion, security, and legal records for up to six years. We do not use them to personalize Practice.",
    },
    {
      p: "Analytics records are deleted 12 months after they are collected. Because they are not linked to your account, we cannot tell which of them relate to you, so a deletion request cannot reach them and they are removed on that schedule instead.",
    },
    { h: "Changes" },
    {
      p: "We will post an updated effective date. We will give advance notice and obtain consent where required before materially changing the data collected, its purposes, or recipient categories.",
    },
    { h: "Contact and appeal" },
    { address: CONTACT_ADDRESS },
    {
      p: `Use the subject "Consumer Health Data Request" for a request and "Consumer Health Data Appeal" for an appeal.`,
    },
  ],
};

export const LEGAL_DOCS: Record<LegalDocId, LegalDocument> = {
  terms: TERMS,
  privacy: PRIVACY,
  health: CONSUMER_HEALTH,
};

/* The consent sentences, kept beside the documents they point at because the
   consent record has to store the exact words the user was shown. Counsel is
   explicit that these are two separate acceptances and that the health-data
   one must not be bundled into the Terms. */
export const CONSENT_TERMS =
  "I am 18 or older and agree to the Terms of Use. I acknowledge the Privacy Policy.";
export const CONSENT_HEALTH =
  "I consent to Lab 10 collecting and using the health areas, topics, experts, practices, choices, and activity I provide to personalize and operate Practice, as described in the Consumer Health Data Privacy Policy. I can withdraw this consent and delete my data at any time. Practice cannot create a personalized account without this data.";

// Shown near the account-creation button, and in the footer of every screen.
export const PRODUCT_DISCLOSURE =
  "Practice uses automated systems to organize and summarize third-party health content. It is not medical care. Experts shown are not necessarily affiliated with or endorsing Practice.";
/* Counsel's footer line (launch package, "Footer disclosure"), with two edits
   of ours, both 2 Aug 2026 and both Rob's.

   First, it ends "endorsing Practice or Lab 10". The public Crossfire pages
   were carrying this and NO_AFFILIATION one under the other, which is the same
   disclaimer said twice, and the only thing the second said that the first did
   not was that the company is covered too. Naming both here retired the
   pairing.

   Second, "not necessarily affiliated" became "not affiliated". Counsel's
   hedge is the one you write when some of the people named might be partners;
   nobody on this roster has been contacted, none has consented, and none is
   paid, so the hedge was describing a relationship we do not have and
   softening a statement we can make flatly. It now says what NO_AFFILIATION
   has always said on the profiles, which is the plain fact.

   FLAG: both are changes to counsel's own wording and are on the list for
   them to bless (docs/legal-launch-engineering.md section 3). If either is
   ever untrue, this line has to go back to the hedge on the same day. */
export const FOOTER_DISCLOSURE =
  "Practice uses automated systems to organize and summarize third-party health content. It is not medical advice, diagnosis, or treatment. Experts shown are not affiliated with or endorsing Practice or Lab 10.";
/* Beside the expert wall, on every expert profile, and on any marketing use
   of a name or face (counsel's launch note 11). Nobody on the roster has
   been contacted or has consented, so every surface built out of their
   identity has to say what the relationship is not. */
export const NO_AFFILIATION =
  "Experts shown are sources, not partners. They are not affiliated with Lab 10 and do not endorse Practice.";

// Shown before or when a visitor first picks health areas or experts.
export const NOTICE_AT_COLLECTION =
  "Practice collects your email, health areas, topics, experts, and activity to create and remember a personalized practice, secure your account, and provide support. We do not sell this data or use it for targeted advertising. We keep it while your account is open and remove it from live systems after deletion. Protected backups may take up to six months to expire.";
